define('core/views/TextareaView',[  // eslint-disable-line requirejs/amd-function-arity
    'underscore',
    'jquery',
    'backbone',

    'core/utils',
    'core/utils/html/nodeTypes',
    'core/CappedStorage',

    // imported for side effects
    'core/extensions/jquery.autoresize',
], function (
    _,
    $,
    Backbone,

    utils,
    nodeTypes,
    CappedStorage
) {
    'use strict';

    var TextareaView = Backbone.View.extend({
        events: {
            'keydown  [data-role=editable]': 'handleKeyDown',
            'keyup    [data-role=editable]': 'handleKeyUp',
            'paste    [data-role=editable]': 'handlePaste',
            'focusin  [data-role=editable]': 'handleFocusIn',
            'blur     [data-role=editable]': 'handleBlur',
        },

        initialize: function (options) {
            options = options || {};
            this.storageKey = options.storageKey;
            this.value = options.value || this.getDraft()[0];
            this.history = [this.value];
            this.historyPosition = 0;
            this.placeholder = options.placeholder;
            this.selectionIndices = {
                start: 0,
                end: 0,
                endElemInd: 0,
                endElemSelectionInd: 0,
                startElemInd: 0,
                startElemSelectionInd: 0,
            };
            this.inputFixed = false;

            this.listenTo(this, 'keychange', _.debounce(this.saveDraft, this.constructor.SAVE_DRAFT_INTERVAL));
        },

        render: function () {
            this.$input = this.createInput();
            this.set(this.value);
            this.$el.append(this.$input);

            // Note: this plugin must be applied after the element
            // is on the DOM
            this.$input.autoresize({ maxHeight: this.constructor.MAX_TEXTAREA_HEIGHT });

            return this;
        },

        createInput: function () {
            return $('<textarea>').attr({
                'class': 'textarea',
                'placeholder': this.placeholder,
                'data-role': 'editable',
            });
        },

        resize: function () {
            // Just need to trigger 'paste' on textarea in order
            // to invoke autoresize plugin
            // -- NOTE: We don't use 'change' because contentEditable doesn't
            //          support the 'change' event in IE, and it throws an error
            this.$input.trigger('paste', { fake: true });
        },

        get: function () {
            return this.$input.val().replace(/^\s+|\s+$/g, ''); // trim the text;
        },

        getSelected: function () {
            var inputEl = this.$input[0];
            if (typeof inputEl.selectionStart === 'number')
                return this.$input.val().substring(inputEl.selectionStart, inputEl.selectionEnd);

            return '';
        },

        /**
         * Returns index of cursor in text returned by .get.
         *
         * Ex. If the text box contains
         *     The[cursor] quick brown fox jumps over...
         * Then .offset() will return 3.
         *
         * @returns {number} index of cursor in text
         */
        offset: function () {
            var inputEl = this.$input[0];
            var out = this.$input.val();
            var offset = typeof inputEl.selectionStart === 'number' ?
                inputEl.selectionStart :
                0;

            // Correct for trailing whitespace
            var trailingSpaces = out.match(/\s+$/);
            if (trailingSpaces) {
                var trailingSpaceCount = trailingSpaces[0].length;
                offset = Math.min(offset, out.length - trailingSpaceCount);
            }

            // Correct for leading whitespace
            var leadingSpaces = out.match(/^\s+/);
            if (leadingSpaces) {
                var leadingSpaceCount = leadingSpaces[0].length;
                offset = Math.max(offset - leadingSpaceCount, 0);
            }

            return offset;
        },

        insertAtCursor: function (str) {
            this.focus();

            var text = this.get();
            var offset = this.offset();
            var newText = utils.insertWithWhitespace(text, offset, str);
            var inputEl = this.$input[0];
            this.set(newText);

            // Put the cursor after the inserted text
            if (inputEl.setSelectionRange) {
                // The + 1 below is for the trailing whitespace of insertWithWhitespace
                var newCursorIndex = newText.indexOf(str, offset) + str.length + 1;
                inputEl.setSelectionRange(newCursorIndex, newCursorIndex);
            }
        },

        insertAroundSelection: function (beforeText, afterText) {
            this.focus();

            var inputEl = this.$input[0];

            var start, end;
            if (typeof inputEl.selectionStart === 'number') {
                start = inputEl.selectionStart;
                end = inputEl.selectionEnd;
            } else {
                start = end = 0;
            }

            var text = this.get();

            var newText = text.substring(0, start) +
                beforeText +
                text.substring(start, end) +
                afterText +
                text.substring(end);

            this.set(newText);

            // Keep the selection around the original text
            if (inputEl.setSelectionRange)
                inputEl.setSelectionRange(start + beforeText.length, end + beforeText.length);
        },

        set: function (str) {
            this.$input.val(str);
        },

        clear: function () {
            this.set('');
        },

        focus: function () {
            this.$input.focus();
        },

        handleKeyDown: function (evt) {
            this.trigger('keydown', evt);
        },

        handleKeyUp: function (evt) {
            this.trigger('keychange', evt);
        },

        handlePaste: function (_evt, options) {
            options = options || {};
            this.trigger(options.fake ? 'paste' : 'paste keychange');
            this.$input.trigger('resize');
        },

        handleFocusIn: function () {
            // need to fix the html elements (like we do in set selection)
            // mouse up is not being called when focusing in for the first time
            this.fixInputStructure();
            this.trigger('focus');
        },

        handleBlur: function () {
            this.trigger('blur');
        },

        /**
         * Save the textarea value plus some metadata to local storage
         * Storage format: [ value, timestamp, etc. ]
         * @returns {void}
         */
        saveDraft: function () {
            if (!this.storageKey)
                return;

            // If text box is empty, don't save. Remove draft.
            if (!$.trim(this.get()))
                return void this.removeDraft();

            this.constructor.storage.set(
                this.storageKey,
                this.toJSON()
            );
        },

        /**
         * Serialize for this.saveDraft()
         * @returns {Array} An array of data to put into local storage
         */
        toJSON: function () {
            return [
                this.get(),
                $.now(),  // timestamp, to expire old values
            ];
        },

        /**
         * Gets the array from local storage that contains the draft string,
         * as well as metadata (e.g., timestamp)
         *
         * The first array element is a string that represents the saved draft that the
         * user was working on in the textarea.
         *
         * @returns {Array} an array whose first element is the draft string
         */
        getDraft: function () {
            var record = [''];

            if (!this.storageKey)
                return record;

            var val = this.constructor.storage.get(this.storageKey);
            if (!val)
                return record;

            record = val;
            if (!record.length)
                return [''];

            var olderThanOneDay = $.now() - record[1] >= this.constructor.DRAFT_MAX_AGE;

            if (olderThanOneDay) {
                // Record has expired; remove it from storage
                this.removeDraft();
                return [''];
            }

            return record;
        },

        removeDraft: function () {
            if (!this.storageKey)
                return;

            this.constructor.storage.remove(this.storageKey);
        },

        fixInputStructure: function () {
            var parentNode, pNode, pNodeReplacement, numOfPsChildren, numOfDivsChildren, node, i;
            var shouldFocusEnd = false;
            var shouldFocusSpecificIndicies = false;
            var document = window.document;

            var inputEl = this.$input[0];

            if (inputEl.type && inputEl.type === 'textarea') {
                // This happens when you focus an being-edited comment, we can skip this since textarea has content
                // and not elements

            } else if (utils.browser.isChrome()) {
                // chrome
                /*
                * If the div only has <br> as <p>s first child (no text in chrome)
                * The structure is as:
                * <div>
                *     <p>
                *         <br>
                *     </p>
                * </div>
                *
                * When the comment box is empty and the user continues to delete, then the structure is as:
                * <div>
                * </div>
                *
                * The structure is otherwise as:
                * <div>
                *     <p>
                *         ...<br>
                *         ...<br>
                *         ...
                *     </p>
                * </div>
                *
                * will replace <br>s with #text '\n' nodes so that it is easy to work with and no different in view
                *
                * <div>
                *     <p>
                *         #text(#text'\n')
                *         #text(#text'\n')
                *         ...
                *         #text
                *     </p>
                * </div
                *
                * */
                if (inputEl.childNodes.length === 0) {
                    pNode = inputEl.appendChild(document.createElement('p'));
                    pNode.appendChild(document.createTextNode('\n'));
                    // The text node needs to contain something inside it so that when the user
                    // actually types something it will be entered into that text node, or else the
                    // user entered text will be a separate text node that's a sibling to the p node.
                    shouldFocusEnd = true;
                } else {
                    parentNode = inputEl.childNodes[0];
                    // For chrome for easier handling replace all the <br> nodes with #text nodes
                    parentNode.childNodes.forEach(function (node) {
                        if (nodeTypes.isBr(node))
                        // This works in chrome but doesn't in FF, doesn't create new lines, piles on the same line
                            parentNode.replaceChild(document.createTextNode('\n'), node);
                    });
                }

            } else if (utils.browser.isIE()) {
                /*
                * Empty when loaded
                * <div>
                *     <p>
                *         <br> -> convert this to <p>#text('')<p>
                *     </p>
                * </div>
                *
                * One line when typing or when loaded
                * <div>
                *     <p>
                *         #text<br> -> convert this to <p>#text<p>
                *     </p>
                * </div>
                *
                * Everything deleted
                * <div>
                * </div>
                *
                * Multiline when typing
                * <div>
                *     (<p>#text</p>)*    #text is not be there for empty lines (just <p></p>)
                *     <p>#text<br></p>
                * </div>
                *
                * Multiline when loaded
                * <div>
                *     <p>
                *         (#text<br>)*
                *     </p>
                * </div>
                *
                * Convert everything to
                * <div>
                *     (<p>#text</p>)*    #text will always be there
                * </div>
                * */

                if (inputEl.childNodes.length === 0) {
                    pNodeReplacement = document.createElement('p');
                    pNodeReplacement.appendChild(document.createTextNode(''));
                    inputEl.appendChild(pNodeReplacement);
                    shouldFocusEnd = true;

                } else {
                    numOfDivsChildren = inputEl.childNodes.length;
                    for (i = 0; i < numOfDivsChildren; i++) {

                        // take each pNode
                        if (nodeTypes.isP(inputEl.childNodes[i])) {
                            numOfPsChildren = inputEl.childNodes[i].childNodes.length;

                            if (numOfPsChildren === 0) {
                                // <p></p>  ->  to  -> <p>#text</p>
                                inputEl.childNodes[i].appendChild(document.createTextNode(''));
                                // place the cursor at the end if we make it <div><p>#text('')</p></div>
                                // so that we keep all the elements within <div>
                                if (numOfDivsChildren === 1) shouldFocusEnd = true;

                            } else if (numOfPsChildren === 1) {
                                // <p><br></p> ->  to  -> <p>#text</p>
                                if (nodeTypes.isBr(inputEl.childNodes[i].childNodes[0])) {
                                    inputEl.childNodes[i].replaceChild(document.createTextNode(''), inputEl.childNodes[i].childNodes[0]);
                                    // place the cursor at the end if we make it <div><p>#text('')</p></div>
                                    // so that we keep all the elements within <div>
                                    if (numOfDivsChildren === 1) shouldFocusEnd = true;
                                }

                            } else {
                                // <p>#text<br></p> Single line (last line) while typing or when draft is loaded
                                // <p>(#text<br>)*</p> multi line when draft is loaded
                                // ->  to  ->
                                // (<p>#text</p>)*
                                while (numOfPsChildren > 0) {
                                    // insert empty #text nodes inside empty <p> nodes to reference for everything
                                    node = inputEl.childNodes[i].childNodes[0];

                                    if (numOfPsChildren === 1 && nodeTypes.isBr(node)) {
                                        inputEl.childNodes[i].replaceChild(document.createTextNode(''), node);
                                        numOfPsChildren -= 1;

                                    } else if (numOfPsChildren === 2 // eslint-disable-line no-magic-numbers
                                        && nodeTypes.isBr(node)
                                        && nodeTypes.isText(inputEl.childNodes[i].childNodes[1])
                                        && this.inputFixed) {
                                        // this is when user presses shift + enter in IE a <br> is inserted
                                        // before the text node in this <p> node, we can add this node if this is not
                                        // multiline draft when page is reloaded (ie. multiline reloaded)
                                        pNodeReplacement = document.createElement('p');
                                        pNodeReplacement.appendChild(document.createTextNode(''));
                                        inputEl.insertBefore(pNodeReplacement, inputEl.childNodes[i]);
                                        i += 1;
                                        numOfDivsChildren += 1;
                                        inputEl.childNodes[i].removeChild(node);
                                        numOfPsChildren -= 2; // eslint-disable-line no-magic-numbers
                                        // will be done with this <p> node after this

                                        this.selectionIndices = {
                                            startElemInd: i,
                                            endElemInd: i,
                                            startElemSelectionInd: 0,
                                            endElemSelectionInd: 0,
                                        };
                                        shouldFocusSpecificIndicies = true;


                                    } else if (numOfPsChildren === 2 // eslint-disable-line no-magic-numbers
                                        && nodeTypes.isText(node)
                                        && nodeTypes.isBr(inputEl.childNodes[i].childNodes[1])) {
                                        if (this.inputFixed) {
                                            pNodeReplacement = document.createElement('p');
                                            pNodeReplacement.appendChild(document.createTextNode(''));
                                            if (i === numOfDivsChildren - 1)
                                                inputEl.appendChild(pNodeReplacement);
                                            else
                                                inputEl.insertBefore(pNodeReplacement, inputEl.childNodes[i + 1]);
                                            numOfDivsChildren += 1;

                                            this.selectionIndices = {
                                                startElemInd: i + 1,
                                                endElemInd: i + 1,
                                                startElemSelectionInd: 0,
                                                endElemSelectionInd: 0,
                                            };
                                            shouldFocusSpecificIndicies = true;
                                        }

                                        inputEl.childNodes[i].removeChild(inputEl.childNodes[i].childNodes[1]);
                                        numOfPsChildren -= 2; // eslint-disable-line no-magic-numbers

                                    } else if (numOfPsChildren > 2 // eslint-disable-line no-magic-numbers
                                        && nodeTypes.isText(node)
                                        && nodeTypes.isBr(inputEl.childNodes[i].childNodes[1])) {

                                        pNodeReplacement = document.createElement('p');
                                        pNodeReplacement.appendChild(document.createTextNode(node.nodeValue));
                                        inputEl.insertBefore(pNodeReplacement, inputEl.childNodes[i]);
                                        i += 1;
                                        numOfDivsChildren += 1;
                                        inputEl.childNodes[i].removeChild(inputEl.childNodes[i].childNodes[1]);
                                        inputEl.childNodes[i].removeChild(node);
                                        numOfPsChildren -= 2; // eslint-disable-line no-magic-numbers

                                    } else {
                                        numOfPsChildren -= 1;
                                    }
                                }
                            }
                        }
                    }
                }

            } else if (utils.browser.isFirefox()) {

                // the p tags can be sometimes div s when everything has been deleted
                /*
                * FF is unique
                * empty loaded fine  can go to <br> collection
                * <div>
                *     <p>
                *         <br>
                *     </p>
                * </div>
                *
                * one line typing or reloaded fine can go to <br> collection
                * <div>
                *     <p>
                *         #text<br>
                *     </p>
                * </div>
                *
                * one line typing after deleting everything and typing
                * CONVERT THIS TO one line typing or reloaded fine
                * since the this.get will be an empty string, the text will be focused
                * <div>
                *     #text<br>
                * </div>
                *
                * empty erased
                * This will be the same as the one above
                * <div>
                * </div>
                *
                * empty erased with selecting all
                * <div>
                *     <br>
                * </div>
                *
                * multiline while typing
                * <div>
                *     (<p> #text <p> || <p> <br> </p>)*
                *     </p> #text<br> </p>
                * </div>
                *
                * multiline when fresh loaded
                * multiple line breaks get combined to 1 line break
                * leaving max 2 linebreaks in a row
                * CONVERTING THIS TO THE multiline while typing OPTION
                * <div>
                *     <p>
                *         (#text <br> || <br>)*
                *         #text
                *     </p>
                * </div>
                *
                * multiline typing after erasing everything
                * <div>
                *     (<div> #text <div> || <div> <br> </div>)*
                *     </div> #text<br> </div>
                * </div>
                * */


                // FRESH MULTILINE CONVERT THIS TO TYPING WHILE MULTILINE
                if (inputEl.childNodes.length === 1 &&
                    nodeTypes.isP(inputEl.childNodes[0]) &&
                    inputEl.childNodes[0].childNodes.length > 2) {
                    // convert multiline fresh loaded to multiline while typing
                    // after text node there should be a <br> node
                    // eliminate <br> node, put text node in a <p> node
                    // if there is a <br> node next put it in a <p> node
                    // add a final <br> node with <p> around it if it is not there

                    numOfPsChildren = inputEl.childNodes[0].childNodes.length;
                    for (i = 0; i < numOfPsChildren; i++) {
                        node = inputEl.childNodes[0].childNodes[i];

                        if (nodeTypes.isText(node)) {
                            pNodeReplacement = document.createElement('p');
                            pNodeReplacement.appendChild(document.createTextNode(node.nodeValue));
                            if (i < numOfPsChildren - 1 && nodeTypes.isBr(inputEl.childNodes[0].childNodes[i + 1]))
                                i += 1;

                        } else if (nodeTypes.isBr(node)) {
                            pNodeReplacement = document.createElement('p');
                            pNodeReplacement.appendChild(document.createElement('br'));

                        }

                        inputEl.appendChild(pNodeReplacement);
                    }

                    inputEl.removeChild(inputEl.childNodes[0]);

                } else if (inputEl.childNodes.length === 1 && nodeTypes.isBr(inputEl.childNodes[0])) {
                    /*
                    * <div>
                    *     <br>
                    * </div>
                    *
                    * -> to ->
                    *
                    * <div>
                    *     <p>
                    *         <br>
                    *     </p>
                    * </div>
                    * */

                    inputEl.removeChild(inputEl.childNodes[0]);
                    pNodeReplacement = document.createElement('p');
                    pNodeReplacement.appendChild(document.createElement('br'));
                    inputEl.appendChild(pNodeReplacement);

                } else if (inputEl.childNodes.length === 0) {
                    /*
                    * <div>
                    * </div>
                    *
                    * -> to ->
                    *
                    * <div>
                    *     <p>
                    *         <br>
                    *     </p>
                    * </div>
                    * */

                    pNodeReplacement = document.createElement('p');
                    pNodeReplacement.appendChild(document.createElement('br'));
                    inputEl.appendChild(pNodeReplacement);
                    shouldFocusEnd = true;
                }

                for (i = 0; i < inputEl.childNodes.length; i++) {
                    var currPNode = inputEl.childNodes[i];
                    if (nodeTypes.isP(currPNode) && currPNode.childNodes.length === 1 && nodeTypes.isBr(currPNode.childNodes[0])) {
                        // this is what we want to set as #text + br
                        /*
                        * all of the
                        * <p>
                        *     <br>
                        * </p>
                        * -> to ->
                        * <p>
                        *     #text('')<br>
                        * </p>
                        * */

                        currPNode.insertBefore(document.createTextNode(''), currPNode.childNodes[0]);

                    } else if (nodeTypes.isDiv(currPNode)) {

                        pNodeReplacement = document.createNode('P');

                        if (currPNode.childNodes.length === 1 && nodeTypes.isBr(currPNode.childNodes[0])) {
                            pNodeReplacement.appendChild(document.createTextNode(''));
                            pNodeReplacement.appendChild(document.createNode('BR'));

                        } else if (currPNode.childNodes.length === 2
                            && nodeTypes.isText(currPNode.childNodes[0])
                            && currPNode.childNodes[0].nodeValue.length > 0
                            && nodeTypes.isBr(currPNode.childNodes[1])) {
                            pNodeReplacement.appendChild(document.createTextNode(currPNode.childNodes[0].nodeValue));
                        }

                        inputEl.replaceChild(pNodeReplacement, currPNode);

                    }
                }


            } else if (utils.browser.isSafari() || utils.browser.isEdge()) {

                /*
                * safari & edge are similar to chrome
                * empty loaded
                * <div>
                *     <p>
                *         <br>
                *     </p>
                * </div>
                *
                * empty erased -> replace for Edge & Safari Different
                * <div>
                *     (<br>) -> Edge has the br Safari doesnt
                * </div>
                * -> replace with
                * <div>
                *     <p>
                *         #text
                *     </p>
                * <div>
                * then focus on the element
                *
                * erased -> and then typing (this shoud not happen b/c option 2 fixes)
                * <div>
                *     #text
                * </div>
                *
                * erased -> and then typing multiline (this shoud not happen b/c option 2 fixes)
                * <div>
                *     #text<br>
                *     #text<br>
                * </div>
                *
                * one line typing
                * <div>
                *     <p>
                *         #text
                *     </p>
                * </div>
                *
                * oneline typing then press enter
                * <div>
                *     <p>
                *         (#text<br>)*
                *         <br>
                *     </p>
                * </div>
                *
                * multiline typing for Edge & Safari Different
                * <div>
                *     <p>
                *         (#text<br>)*
                *         #text (<br>) <- Safari doesnt have this last br (replaces when trying to type)
                *     </p>
                * </div>
                *
                * multiline loaded
                * <div>
                *     <p>
                *         ((#text)(<br>)*)*
                *     </p>
                * </div>
                *
                * multiline
                * <div>
                *     <p>
                *         (#text<br>)*
                *         #text || <br> (depending on empty or not last line)
                *     </p>
                * </div>
                *
                * convert everything to
                * <div>
                *     <p>
                *         (#text<br>)*
                *     </p>
                * </div>
                *
                * */

                if (inputEl.childNodes.length === 0 || !nodeTypes.isP(inputEl.childNodes[0])) {
                    /* This is for when you keep deleting an empty textarea, Safari and Edge has some differences
                    * but if the first child of inputEl is not <p> reset it
                    * <div></div> || <div>(<br>)*</div>
                    * -> replace with
                    * <div>
                    *     <p>
                    *         (#text('')<br>)*
                    *     </p>
                    * <div>
                    */
                    pNodeReplacement = document.createElement('p');
                    pNodeReplacement.appendChild(document.createTextNode(''));
                    pNodeReplacement.appendChild(document.createElement('br'));
                    while (inputEl.firstChild)
                        inputEl.removeChild(inputEl.firstChild);
                    inputEl.appendChild(pNodeReplacement);
                    shouldFocusEnd = true;
                } else {
                    /* make everything
                    * <div>
                    *     <p>
                    *         (#text<br>)* (there might not be a last br)
                    *     </p>
                    * <div> */

                    parentNode = inputEl.childNodes[0];
                    numOfPsChildren = parentNode.childNodes.length;
                    for (i = 0; i < numOfPsChildren; i += 1) {
                        // for all the br nodes that either have another br before them || the first child that is br
                        if (nodeTypes.isBr(parentNode.childNodes[i])
                            && (i === 0 || nodeTypes.isBr(parentNode.childNodes[i - 1]))) {
                            parentNode.insertBefore(document.createTextNode(''), parentNode.childNodes[i]);
                            // to avoid duplicating the #text nodes when replacing the last <br>
                            if (i === numOfPsChildren - 1) shouldFocusEnd = true;
                        }
                    }

                    for (i = 0; i < numOfPsChildren - 1; i += 1) {
                        // for all the #text('') nodes that have another #text node after, delete the empty #text node
                        if (nodeTypes.isText(parentNode.childNodes[i])
                            && parentNode.childNodes[i].nodeValue === ''
                            && nodeTypes.isText(parentNode.childNodes[i + 1])) {
                            parentNode.removeChild(parentNode.childNodes[i]);
                            i -= 1;
                            numOfPsChildren -= 1;
                        }
                    }
                }

            } else if (utils.browser.isOpera()) {

                /*
                * Empty when loaded
                * <div>
                *     <p>
                *         <br>
                *     </p>
                * </div>
                *
                * Single line when loaded
                * <div>
                *     <p>
                *         #text<br>
                *     </p>
                * </div>
                *
                * Single line when typing
                * <div>
                *     <p>
                *         #text<br>
                *     </p>
                * </div>
                *
                * When everything is erased
                * make this similar to empty when loaded
                * <div>
                * </div>
                *
                * Multiline while typing
                * <div>
                *     <p>
                *         (#text)*(#text'\n')*
                *     </p>
                * </div>
                *
                * Multiline when loaded
                *  -> replace <br>s with #text'\n's
                * <div>
                *     <p>
                *         (#text)*(<br>)*
                *     </p>
                * </div>
                *
                * replace all <br>s with #text'\n'
                * */

                if (inputEl.childNodes.length === 0) {
                    pNodeReplacement = document.createElement('p');
                    pNodeReplacement.appendChild(document.createTextNode('\n'));
                    inputEl.appendChild(pNodeReplacement);
                    shouldFocusEnd = true;

                } else {
                    parentNode = inputEl.childNodes[0];
                    for (i = 0; i < parentNode.childNodes.length; i++) {
                        if (nodeTypes.isBr(parentNode.childNodes[i]))
                            parentNode.replaceChild(document.createTextNode('\n'), parentNode.childNodes[i]);
                    }
                }
            }

            this.inputFixed = true;

            if (shouldFocusSpecificIndicies)
                this.selectText();
            else if (shouldFocusEnd)
                this.focusEndOfText();
        },

        focusEndOfText: function () {
            var inputEl = this.$input[0];
            var parentNode;
            var startElemInd = 0;
            var endElemInd = 0;
            var startElemSelectionInd = 0;
            var endElemSelectionInd = 0;


            if (utils.browser.isChrome() || utils.browser.isOpera()) {
                /* <div>
                *      <p>
                *         (#text)*
                *     </p>
                * </div> */
                parentNode = inputEl.childNodes[0];
                startElemInd = endElemInd = parentNode.childNodes.length - 1;
                startElemSelectionInd = endElemSelectionInd = parentNode.childNodes[startElemInd].nodeValue.length;

            } else if (utils.browser.isSafari() || utils.browser.isEdge()) {
                /* <div>
                *      <p>
                *         (#text<br>)* (there might not be a last br)
                *     </p>
                * </div> */
                parentNode = inputEl.childNodes[0];
                startElemInd = endElemInd = nodeTypes.isText(parentNode.childNodes[parentNode.childNodes.length - 1]) ?
                    parentNode.childNodes.length - 1 :
                    parentNode.childNodes.length - 2; // eslint-disable-line no-magic-numbers
                startElemSelectionInd = endElemSelectionInd = parentNode.childNodes[startElemInd].nodeValue.length;

            } else if (utils.browser.isFirefox() || utils.browser.isIE()) {
                /* <div>
                *     (<p>#text</p>)*    #text will always be there
                * </div> */
                startElemInd = endElemInd = inputEl.childNodes.length - 1;
                startElemSelectionInd = inputEl.childNodes[startElemInd].childNodes[0].nodeValue.length;
                endElemSelectionInd = startElemSelectionInd;
            }

            this.selectionIndices = {
                startElemInd: startElemInd,
                startElemSelectionInd: startElemSelectionInd,
                endElemInd: endElemInd,
                endElemSelectionInd: endElemSelectionInd,
            };
            this.selectText();
        },

        getWhichChildIndex: function (parent, child) {
            var i = 0;
            for (; i < parent.childNodes.length; i++) {
                if (parent.childNodes[i] === child)
                    return i;
            }
            return -1;
        },

        getNodeLength: function (node) {
            // This function will be useful esp. for FF, don't like to see the same macro everywhere
            return nodeTypes.isBr(node) ? 1 : node.nodeValue.length;
        },

        placeholderSetSelection: function () {
            var selectionStart, selectionEnd;
            var editorSelection = window.getSelection();

            if (this.$input[0].type && this.$input[0].type === 'textarea') {
                selectionStart = this.$input[0].selectionStart;
                selectionEnd = this.$input[0].selectionEnd;

            } else if (editorSelection.anchorNode.tagName === 'DIV') {
                // all has been selected
                selectionStart = 0;
                selectionEnd = this.get().length;

            } else if (editorSelection.anchorNode.tagName === 'P') {
                // empty area of textarea has been clicked
                selectionStart = selectionEnd = this.get().length;

            } else {
                selectionStart = Math.min(editorSelection.focusOffset, editorSelection.anchorOffset);
                selectionEnd = Math.max(editorSelection.focusOffset, editorSelection.anchorOffset);
            }

            this.selectionIndices = {
                start: selectionStart,
                end: selectionEnd,
                endElemInd: 0,
                endElemSelectionInd: 0,
                startElemInd: 0,
                startElemSelectionInd: 0,
            };
        },

        setSelection: function () {
            var parentNode, selectionStart, selectionEnd, startElemInd, endElemInd, startElemSelectionInd,
                endElemSelectionInd;

            this.fixInputStructure();

            var inputEl = this.$input[0];
            var shouldSelectText = false;
            var selectionObj = window.getSelection();

            if (inputEl.type && inputEl.type === 'textarea') {
                // for editing posts that are already commented (uses <textarea> different than the div.textarea)
                // Dont need to set these b/c they are not used in textarea
                // startElemInd, endElemInd, startElemSelectionInd, endElemSelectionInd;
                selectionStart = inputEl.selectionStart;
                selectionEnd = inputEl.selectionEnd;

            } else if (selectionObj.rangeCount) {
                var selectionRange = selectionObj.getRangeAt(0);

                if (utils.browser.isChrome()) {
                    ////////////////////////////////////////////////////////////////////////////////////////////////////
                    //                                             CHROME
                    ////////////////////////////////////////////////////////////////////////////////////////////////////
                    /* the structure is
                    * <div>
                    *     <p>
                    *         #text(#text'\n')
                    *         #text(#text'\n')
                    *         ...
                    *         #text
                    *     </p>
                    * </div
                    *
                    * will capture the indicies of the text elements and the selection indicies within the elements
                    * */

                    parentNode = inputEl.childNodes[0];

                    // Tally up the nodes lengths up to the starting node as well as find starting node
                    selectionStart = startElemInd = 0;
                    while (parentNode.childNodes[startElemInd] !== selectionRange.startContainer) {
                        selectionStart += this.getNodeLength(parentNode.childNodes[startElemInd]);
                        startElemInd += 1;
                    }

                    // the caret position within the element
                    startElemSelectionInd = selectionRange.startOffset;
                    // selection within the element, which was not accounted for in the loop
                    selectionStart += selectionRange.startOffset;


                    if (selectionRange.collapsed) {
                        // selection is a caret, end values are the same as start values
                        endElemInd = startElemInd;
                        selectionEnd = selectionStart;
                        endElemSelectionInd = startElemSelectionInd;

                    } else if (selectionRange.startContainer === selectionRange.endContainer) {
                        // selection is within the same element, just add the endOffset to start values for end values
                        endElemInd = startElemInd;
                        endElemSelectionInd = selectionRange.endOffset;
                        // start offset has been added to the selection start value, just add the difference
                        selectionEnd = selectionStart + (selectionRange.endOffset - selectionRange.startOffset);

                    } else {
                        // selection is through multiple elements find the endContainer's index
                        // start from start element
                        endElemInd = startElemInd;
                        // remove the already added startOffset
                        selectionEnd = selectionStart - selectionRange.startOffset;

                        // Keep tallying up the lengths of the nodes from startContainer to end
                        while (parentNode.childNodes[endElemInd] !== selectionRange.endContainer) {
                            selectionEnd += this.getNodeLength(parentNode.childNodes[endElemInd]);
                            endElemInd += 1;
                        }
                        // after finding the starting node and ending node, if the ending node is a break node, go back
                        // to the end of the previous node. This can be done in the while loop above but more explicit
                        // this way
                        if (nodeTypes.isNewline(parentNode.childNodes[endElemInd])) {
                            // the end index is the end of the previous element
                            endElemSelectionInd = this.getNodeLength(parentNode.childNodes[endElemInd - 1]);
                            endElemInd -= 1;
                        } else {
                            // the selection within the end container which was not accounted for in the loop
                            selectionEnd += selectionRange.endOffset;
                            endElemSelectionInd = selectionRange.endOffset;
                        }
                    }
                } else if (utils.browser.isIE()) {
                    ////////////////////////////////////////////////////////////////////////////////////////////////////
                    //                                               IE
                    ////////////////////////////////////////////////////////////////////////////////////////////////////
                    /*
                    The structure is
                    * <div>
                    *     (<p>#text</p>)*    #text will always be there
                    *     <p>#text<br></p>
                    * </div>
                    * */

                    if (nodeTypes.isDiv(selectionRange.startContainer)
                        && nodeTypes.isDiv(selectionRange.endContainer)
                        && selectionRange.collapsed) {

                        // when first focusing on the textbox (needs to be reloaded multiline for the most part)
                        // by just clicking and not dragging, ie. not selecting & just focusing
                        startElemInd = inputEl.childNodes.length - 1;
                        endElemInd = inputEl.childNodes.length - 1;
                        startElemSelectionInd = inputEl.childNodes[startElemInd].childNodes[0].nodeValue.length;
                        endElemSelectionInd = inputEl.childNodes[endElemInd].childNodes[0].nodeValue.length;

                        // we set the selection at the very end b/c we don't know where the user clicked to focus in
                        shouldSelectText = true; // a way to focus on the very end here

                    } else if (nodeTypes.isDiv(selectionRange.startContainer)
                        && nodeTypes.isDiv(selectionRange.endContainer)
                        && !selectionRange.collapsed) {

                        // when first focusing on the textbox (needs to be reloaded multiline for the most part)
                        // by clicking and dragging so selecting & focusing at the same time
                        startElemInd = selectionRange.startOffset;
                        endElemInd = selectionRange.endOffset - 1; // this one is the end container index + 1
                        // selectionRange returns as [s, e) we use as [s, e] but the selection indices [sSel, eSel)
                        startElemSelectionInd = 0;
                        endElemSelectionInd = inputEl.childNodes[endElemInd].childNodes[0].nodeValue.length;

                    } else if (nodeTypes.isText(selectionRange.startContainer)
                        && nodeTypes.isText(selectionRange.endContainer)) {

                        startElemInd = this.getWhichChildIndex(inputEl, selectionRange.startContainer.parentNode);
                        endElemInd = this.getWhichChildIndex(inputEl, selectionRange.endContainer.parentNode);
                        startElemSelectionInd = selectionRange.startOffset;
                        endElemSelectionInd = selectionRange.endOffset;

                    } else if (nodeTypes.isText(selectionRange.startContainer)
                        && nodeTypes.isDiv(selectionRange.endContainer)) {

                        startElemInd = this.getWhichChildIndex(inputEl, selectionRange.startContainer.parentNode);
                        endElemInd = selectionRange.endOffset - 1;
                        startElemSelectionInd = selectionRange.startOffset;
                        endElemSelectionInd = inputEl.childNodes[endElemInd].childNodes[0].nodeValue.length;

                    } else {
                        // should never reach here but leave it in case something goes very bad
                        // using all zeros will not be an issue, since we fix the input everytime
                        startElemInd = 0;
                        endElemInd = 0;
                        startElemSelectionInd = 0;
                        endElemSelectionInd = 0;
                    }

                } else if (utils.browser.isFirefox()) {
                    ////////////////////////////////////////////////////////////////////////////////////////////////////
                    //                                            FIRE FOX
                    ////////////////////////////////////////////////////////////////////////////////////////////////////
                    /*
                    * The structure is
                    * <div>
                    *     <p>
                    *         (#text <br> || <br>)*
                    *         #text
                    *     </p>
                    * </div>
                    * */

                    if (nodeTypes.isDiv(selectionRange.startContainer)
                        && nodeTypes.isDiv(selectionRange.endContainer)
                        && selectionRange.startContainer === inputEl
                        && selectionRange.endContainer === inputEl
                        && selectionRange.collapsed) {

                        // when first focusing on the textbox by just clicking and not selecting & focusing
                        startElemInd = inputEl.childNodes.length - 1;
                        endElemInd = inputEl.childNodes.length - 1;
                        startElemSelectionInd = inputEl.childNodes[startElemInd].childNodes[0].nodeValue.length;
                        endElemSelectionInd = inputEl.childNodes[endElemInd].childNodes[0].nodeValue.length;

                        shouldSelectText = true; // a way to focus on the very end here

                    } else if (nodeTypes.isDiv(selectionRange.startContainer)
                        && nodeTypes.isDiv(selectionRange.endContainer)
                        && selectionRange.startContainer === inputEl
                        && selectionRange.endContainer === inputEl
                        && !selectionRange.collapsed) {

                        // ctrl/cmd + a within the textbox, or clicking and drag selection
                        // endoffset becomes the last selected child + 1, like [s, e), but we use it as [s, e]
                        // thus endoffset -1
                        startElemInd = selectionRange.startOffset;
                        endElemInd = selectionRange.endOffset - 1;
                        startElemSelectionInd = 0;
                        endElemSelectionInd = inputEl.childNodes[endElemInd].childNodes[0].nodeValue.length;

                    } else if (nodeTypes.isDiv(selectionRange.startContainer)
                        && nodeTypes.isText(selectionRange.endContainer)
                        && selectionRange.startContainer === inputEl) {

                        // when first focusing on the textbox by just dragging (selecting and focusing)
                        // ending at a #text node
                        startElemInd = 0;
                        endElemInd = this.getWhichChildIndex(inputEl, selectionRange.endContainer.parentNode);
                        startElemSelectionInd = 0;
                        endElemSelectionInd = selectionRange.endOffset;

                    } else if (nodeTypes.isDiv(selectionRange.startContainer)
                        && nodeTypes.isP(selectionRange.endContainer)
                        && selectionRange.startContainer === inputEl) {

                        // when first focusing on the textbox by just dragging (selecting and focusing)
                        // ending at a p node
                        startElemInd = 0;
                        endElemInd = this.getWhichChildIndex(inputEl, selectionRange.endContainer); // use the node (p)
                        startElemSelectionInd = 0;
                        endElemSelectionInd = inputEl.childNodes[endElemInd].childNodes[0].nodeValue.length; // the end of the endContainer's text part

                    } else if (nodeTypes.isText(selectionRange.startContainer)
                        && nodeTypes.isText(selectionRange.endContainer)) {

                        startElemInd = this.getWhichChildIndex(inputEl, selectionRange.startContainer.parentNode);
                        endElemInd = this.getWhichChildIndex(inputEl, selectionRange.endContainer.parentNode);
                        startElemSelectionInd = selectionRange.startOffset;
                        endElemSelectionInd = selectionRange.endOffset;

                    } else if (nodeTypes.isText(selectionRange.startContainer)
                        && nodeTypes.isP(selectionRange.endContainer)) {

                        startElemInd = this.getWhichChildIndex(inputEl, selectionRange.startContainer.parentNode);
                        endElemInd = this.getWhichChildIndex(inputEl, selectionRange.endContainer);
                        startElemSelectionInd = selectionRange.startOffset;
                        endElemSelectionInd = startElemInd === endElemInd ?
                            inputEl.childNodes[endElemInd].childNodes[0].nodeValue.length :
                            0;

                    } else if (nodeTypes.isP(selectionRange.startContainer)
                        && nodeTypes.isText(selectionRange.endContainer)) {

                        // if the end selection index is === 0 we should handle as if it is an all brs selection
                        // if the end selection index is > 0 it looks as if only the end ind much has been selected from
                        // the end element

                        startElemInd = this.getWhichChildIndex(inputEl, selectionRange.startContainer);
                        endElemInd = this.getWhichChildIndex(inputEl, selectionRange.endContainer.parentNode);
                        startElemSelectionInd = 0;
                        endElemSelectionInd = selectionRange.endOffset;

                    } else if (nodeTypes.isP(selectionRange.startContainer)
                        && nodeTypes.isP(selectionRange.endContainer)) {
                        // when triple clicking on a line to select, it selects the entire <p>

                        startElemInd = this.getWhichChildIndex(inputEl, selectionRange.startContainer);
                        endElemInd = this.getWhichChildIndex(inputEl, selectionRange.endContainer);
                        startElemSelectionInd = 0;
                        endElemSelectionInd = inputEl.childNodes[endElemInd].childNodes[0].nodeValue.length;

                    } else {
                        // should never reach here but leave it in case something goes very bad
                        // using all zeros will not be an issue, since we fix the input everytime
                        startElemInd = 0;
                        endElemInd = 0;
                        startElemSelectionInd = 0;
                        endElemSelectionInd = 0;
                    }

                } else if (utils.browser.isSafari() || utils.browser.isEdge()) {
                    ////////////////////////////////////////////////////////////////////////////////////////////////////
                    //                                         SAFARI & EDGE
                    ////////////////////////////////////////////////////////////////////////////////////////////////////
                    /*
                    * The shape is guaranteed to be
                    /* make everything
                    * <div>
                    *     <p>
                    *         (#text<br>)* (there might not be a last br)
                    *     </p>
                    * <div> */

                    // if either container in the selection range is a p we can
                    // easily go to the end of the previous text node

                    parentNode = inputEl.childNodes[0];

                    if (nodeTypes.isText(selectionRange.startContainer)) {
                        // selection starts on a text node this is fine
                        startElemInd = this.getWhichChildIndex(parentNode, selectionRange.startContainer);
                        startElemSelectionInd = selectionRange.startOffset;

                    } else if (nodeTypes.isP(selectionRange.startContainer)) {
                        // start container node is <p> when user holds down shift and presses the arrow keys to select
                        // in this case the #text node of the previous <p> node should be the selection node
                        if (selectionRange.startOffset < parentNode.childNodes.length
                            && nodeTypes.isText(parentNode.childNodes[selectionRange.startOffset])) {
                            startElemInd = selectionRange.collapsed ?
                                selectionRange.startOffset :
                                selectionRange.startOffset - 2; // eslint-disable-line no-magic-numbers
                        } else {
                            startElemInd = selectionRange.startOffset - 1;
                        }

                        startElemSelectionInd = parentNode.childNodes[startElemInd].nodeValue.length;

                    } else if (nodeTypes.isDiv(selectionRange.startContainer)) {
                        if (selectionRange.collapsed) {
                            // When you focus for first time on edge, containers are divs and the range is collapsed
                            startElemInd = nodeTypes.isText(parentNode.childNodes[parentNode.childNodes.length - 1]) ?
                                parentNode.childNodes.length - 1 :
                                parentNode.childNodes.length - 2; // eslint-disable-line no-magic-numbers
                            startElemSelectionInd = parentNode.childNodes[startElemInd].nodeValue.length;

                        } else {
                            // When you ctrl + a on edge, containers are divs and the range is not collapsed
                            startElemInd = 0;
                            startElemSelectionInd = 0;
                        }
                    }

                    if (nodeTypes.isText(selectionRange.endContainer)) {
                        // selection ends on a text node this is fine
                        endElemInd = this.getWhichChildIndex(parentNode, selectionRange.endContainer);
                        endElemSelectionInd = selectionRange.endOffset;

                    } else if (nodeTypes.isP(selectionRange.endContainer)) {
                        // end container node is <p> when user holds down shift and presses the arrow keys to select
                        // in this case the #text node of the previous <p> node should be the selection node
                        if (selectionRange.endOffset < parentNode.childNodes.length
                            && nodeTypes.isText(parentNode.childNodes[selectionRange.endOffset])) {
                            endElemInd = selectionRange.collapsed ?
                                selectionRange.endOffset :
                                selectionRange.endOffset - 2; // eslint-disable-line no-magic-numbers

                        } else if (selectionRange.endOffset - 1 < parentNode.childNodes.length
                            && nodeTypes.isText(parentNode.childNodes[selectionRange.endOffset - 1])) {
                            // the endoffset is the index element of the last <br> choose the #text right before
                            endElemInd = selectionRange.endOffset - 1;
                        } else if (selectionRange.endOffset - 1 < parentNode.childNodes.length
                            && nodeTypes.isBr(parentNode.childNodes[selectionRange.endOffset - 1])) {
                            // the endoffset is right after the <br> node, the index of <br> + 1 to select the text
                            // node right before we need to select two before the offset we get
                            endElemInd = selectionRange.endOffset - 2; // eslint-disable-line no-magic-numbers
                        } else {
                            // should never reach here but a fallback case nonetheless
                            endElemInd = parentNode.childNodes.length - 2; // eslint-disable-line no-magic-numbers
                        }

                        endElemSelectionInd = parentNode.childNodes[endElemInd].nodeValue.length;

                    } else if (nodeTypes.isDiv(selectionRange.endContainer)) {
                        // When you focus for first time on edge, containers are divs and the range is collapsed
                        // When you ctrl + a on edge, containers are divs and the range is not collapsed
                        // in either case end point should be the end of the text
                        endElemInd = nodeTypes.isText(parentNode.childNodes[parentNode.childNodes.length - 1]) ?
                            parentNode.childNodes.length - 1 :
                            parentNode.childNodes.length - 2; // eslint-disable-line no-magic-numbers
                        endElemSelectionInd = parentNode.childNodes[startElemInd].nodeValue.length;
                    }

                } else if (utils.browser.isOpera()) {
                    ////////////////////////////////////////////////////////////////////////////////////////////////////
                    //                                             OPERA
                    ////////////////////////////////////////////////////////////////////////////////////////////////////
                    /*
                    * The structure is
                    * <div>
                    *     <p>
                    *         (#text)*(#text'\n')*
                    *     </p>
                    * </div>
                    * */

                    parentNode = inputEl.childNodes[0];
                    startElemInd = this.getWhichChildIndex(parentNode, selectionRange.startContainer);
                    endElemInd = this.getWhichChildIndex(parentNode, selectionRange.endContainer);
                    startElemSelectionInd = selectionRange.startOffset;
                    endElemSelectionInd = selectionRange.endOffset;

                } else {
                    // TODO: @taylangocmen is there a way to address the other browsers?
                    return this.placeholderSetSelection();
                }
            } else {
                this.focusEndOfText();
            }

            this.selectionIndices = {
                start: selectionStart,
                end: selectionEnd,
                startElemInd: startElemInd,
                startElemSelectionInd: startElemSelectionInd,
                endElemInd: endElemInd,
                endElemSelectionInd: endElemSelectionInd,
            };

            if (shouldSelectText)
                this.selectText();
        },

        addTagTextarea: function (tagName) {
            // this is the old add tag, this will be removed when <div.textarea> and <textarea> are consolidated
            var leftTag, rightTag, editedText;
            var selectionStart = this.selectionIndices.start;
            var selectionEnd = this.selectionIndices.end;
            var startElemInd = this.selectionIndices.startElemInd;
            var endElemInd = this.selectionIndices.endElemInd;
            var startElemSelectionInd = this.selectionIndices.startElemSelectionInd;
            var endElemSelectionInd = this.selectionIndices.endElemSelectionInd;

            switch (tagName) {
            case 'a':
                leftTag = '<a href="#">';
                rightTag = '</a>';
                break;
            default:
                leftTag = '<' + tagName + '>';
                rightTag = '</' + tagName + '>';
            }

            // Dissect the current text to add tags around the selection
            var text = this.get();
            var left = text.slice(0, selectionStart);
            var selectedText = text.slice(selectionStart, selectionEnd);
            var right = text.slice(selectionEnd);

            var leftTagRegex = new RegExp('<' + tagName + '[^<>]*>');
            var rightTagRegex = new RegExp(rightTag + '$');
            var leftTagMatch = selectedText.match(leftTagRegex);
            var rightTagMatch = selectedText.match(rightTagRegex);

            if (leftTagMatch && rightTagMatch &&
                leftTagMatch.index === 0 &&
                rightTagMatch.index === selectionEnd - selectionStart - rightTag.length) {
                // Remove tags from the selected text since the matches are at the beginning & end of selection
                editedText = selectedText.replace(leftTagRegex, '').replace(rightTagRegex, '');
                selectionEnd -= leftTagMatch[0].length + rightTagMatch[0].length;
                endElemSelectionInd -= leftTagMatch[0].length + rightTagMatch[0].length;
            } else {
                // Add tags around the selected text
                editedText = leftTag + selectedText + rightTag;
                selectionEnd += leftTag.length + rightTag.length;
                endElemSelectionInd += leftTag.length + rightTag.length;
            }

            this.selectionIndices = {
                start: selectionStart,
                end: selectionEnd,
                startElemInd: startElemInd,
                endElemInd: endElemInd,
                startElemSelectionInd: startElemSelectionInd,
                endElemSelectionInd: endElemSelectionInd,
            };

            this.set(left + editedText + right);

            this.debouncedSaveHistory();

            this.selectText();
        },

        addTag: function (tagName) {
            /* Select tags are not stripped by the backend, when a text editor button is clicked we take the current
              * text in the editable area and the previously recorded selection (or cursor position if selection range
              * len is 0) and add tags with carets around them as opening and closing tags around the selected text.
              *
              * If the selected text have the match for opening and closing tags with the tagName around them, we
              * remove the tag. */

            var document = window.document;
            var inputEl = this.$input[0];
            var leftTag, rightTag, editedText, leftTagMatch, rightTagMatch, commonReplacementText, leftReplacementText,
                rightReplacementText, parentNode, startElem, endElem, startElemVal, endElemVal;
            var selectionStart = this.selectionIndices.start;
            var selectionEnd = this.selectionIndices.end;
            var startElemInd = this.selectionIndices.startElemInd;
            var endElemInd = this.selectionIndices.endElemInd;
            var startElemSelectionInd = this.selectionIndices.startElemSelectionInd;
            var endElemSelectionInd = this.selectionIndices.endElemSelectionInd;

            // Set the tags as <X> </X>
            switch (tagName) {
            case 'a':
                leftTag = '<a href="#">';
                rightTag = '</a>';
                break;
            default:
                leftTag = '<' + tagName + '>';
                rightTag = '</' + tagName + '>';
            }

            // regex's to match fast, gotta go fast
            var leftTagRegex = new RegExp('<' + tagName + '[^<>]*>');
            // previously tried removing $ to capture the matched in the form of aaa[<x>bbb</x>]ccc
            // but then we catch the first </x> in: aaaa[<x>bbbb<x>ccc</x>dddd</x>]eeeeee
            // ([] indicating the selection start and end) the solution is to match with $ in the substring
            var rightTagRegex = new RegExp(rightTag + '$');

            if (inputEl.type && inputEl.type === 'textarea') {
                return this.addTagTextarea(tagName);

            } else if (utils.browser.isChrome() || utils.browser.isSafari() || utils.browser.isOpera() || utils.browser.isEdge()) {
                parentNode = inputEl.childNodes[0];
                // #text elements in the <p> tag which we will replace, also called anchor elements
                startElem = parentNode.childNodes[startElemInd];
                endElem = parentNode.childNodes[endElemInd];

                // https://stackoverflow.com/questions/21311299/nodevalue-vs-innerhtml-and-textcontent-how-to-choose
                // textContent of the elements, nodeValue is the fastest
                startElemVal = startElem.nodeValue;
                endElemVal = endElem.nodeValue;

                // Matches within the anchor elements, returns an array of matches
                leftTagMatch = startElemVal.slice(startElemSelectionInd).match(leftTagRegex);
                // match with $ in the substring of the selection, the index will be respect to the whole string
                rightTagMatch = endElemVal.slice(0, endElemSelectionInd).match(rightTagRegex);

                // 0 based index of the whole match we use to check if it was indeed our selection indicies
                if (leftTagMatch && rightTagMatch &&
                    leftTagMatch.index === 0 &&
                    rightTagMatch.index === endElemSelectionInd - rightTag.length) {

                    // Remove tags from the selected text since the matches are at the beginning & end of selection
                    if (startElemInd === endElemInd) {
                        // same element only replace once
                        commonReplacementText = startElemVal.slice(0, startElemSelectionInd)
                            + startElemVal.slice(startElemSelectionInd, endElemSelectionInd).replace(leftTagRegex, '').replace(rightTagRegex, '')
                            + startElemVal.slice(endElemSelectionInd);
                        parentNode.replaceChild(document.createTextNode(commonReplacementText), startElem);
                        endElemSelectionInd -= leftTagMatch[0].length + rightTagMatch[0].length;
                    } else {
                        // different elements replace twice
                        leftReplacementText = startElemVal.slice(0, startElemSelectionInd) + startElemVal.slice(startElemSelectionInd).replace(leftTagRegex, '');
                        rightReplacementText = endElemVal.slice(0, endElemSelectionInd).replace(rightTagRegex, '') + endElemVal.slice(endElemSelectionInd);
                        parentNode.replaceChild(document.createTextNode(leftReplacementText), startElem);
                        parentNode.replaceChild(document.createTextNode(rightReplacementText), endElem);
                        endElemSelectionInd -= rightTagMatch[0].length;
                    }

                    // set the end selections for textinput from the edit view
                    selectionEnd -= leftTagMatch[0].length + rightTagMatch[0].length;
                } else {
                    // Add tags around the selected text
                    if (startElemInd === endElemInd) {
                        // same element replaces once
                        commonReplacementText = startElemVal.slice(0, startElemSelectionInd) + leftTag +
                            startElemVal.slice(startElemSelectionInd, endElemSelectionInd) + rightTag +
                            startElemVal.slice(endElemSelectionInd);
                        parentNode.replaceChild(document.createTextNode(commonReplacementText), startElem);
                        endElemSelectionInd += leftTag.length + rightTag.length;
                    } else {
                        // different elements replace twice
                        leftReplacementText = startElemVal.slice(0, startElemSelectionInd) + leftTag +
                            startElemVal.slice(startElemSelectionInd);
                        rightReplacementText = endElemVal.slice(0, endElemSelectionInd) + rightTag +
                            endElemVal.slice(endElemSelectionInd);
                        parentNode.replaceChild(document.createTextNode(leftReplacementText), startElem);
                        parentNode.replaceChild(document.createTextNode(rightReplacementText), endElem);
                        endElemSelectionInd += rightTag.length;
                    }

                    // set the end selections for textinput from the edit view
                    selectionEnd += leftTag.length + rightTag.length;
                }

            } else if (utils.browser.isFirefox() || utils.browser.isIE()) {
                // TODO: @taylangocmen this and the chrome section are very similar try to combine both into one method
                ////////////////////////////////////////////////////////////////////////////////////////////////////
                //                                             FF & IE
                ////////////////////////////////////////////////////////////////////////////////////////////////////

                var startP = inputEl.childNodes[this.selectionIndices.startElemInd];
                var endP = inputEl.childNodes[this.selectionIndices.endElemInd];
                startElem = startP.childNodes[0];
                endElem = endP.childNodes[0];
                startElemVal = startElem.nodeValue;
                endElemVal = endElem.nodeValue;

                // Matches within the anchor elements, returns an array of matches
                leftTagMatch = startElemVal.slice(startElemSelectionInd).match(leftTagRegex);
                // match with $ in the substring of the selection, the index will be respect to the whole string
                rightTagMatch = endElemVal.slice(0, endElemSelectionInd).match(rightTagRegex);

                // 0 based index of the whole match we use to check if it was indeed our selection indicies
                if (leftTagMatch && rightTagMatch &&
                    leftTagMatch.index === 0 &&
                    rightTagMatch.index === endElemSelectionInd - rightTag.length) {

                    // Remove tags from the selected text since the matches are at the beginning & end of selection
                    if (startElemInd === endElemInd) {
                        // same element only replace once
                        commonReplacementText = startElemVal.slice(0, startElemSelectionInd)
                            + startElemVal.slice(startElemSelectionInd, endElemSelectionInd).replace(leftTagRegex, '').replace(rightTagRegex, '')
                            + startElemVal.slice(endElemSelectionInd);
                        startP.replaceChild(document.createTextNode(commonReplacementText), startElem);
                        endElemSelectionInd -= leftTagMatch[0].length + rightTagMatch[0].length;
                    } else {
                        // different elements replace twice
                        leftReplacementText = startElemVal.slice(0, startElemSelectionInd) + startElemVal.slice(startElemSelectionInd).replace(leftTagRegex, '');
                        rightReplacementText = endElemVal.slice(0, endElemSelectionInd).replace(rightTagRegex, '') + endElemVal.slice(endElemSelectionInd);
                        startP.replaceChild(document.createTextNode(leftReplacementText), startElem);
                        endP.replaceChild(document.createTextNode(rightReplacementText), endElem);
                        endElemSelectionInd -= rightTagMatch[0].length;
                    }

                    // set the end selections for textinput from the edit view
                    selectionEnd -= leftTagMatch[0].length + rightTagMatch[0].length;
                } else {
                    // Add tags around the selected text
                    if (startElemInd === endElemInd) {
                        // same element replaces once
                        commonReplacementText = startElemVal.slice(0, startElemSelectionInd) + leftTag +
                            startElemVal.slice(startElemSelectionInd, endElemSelectionInd) + rightTag +
                            startElemVal.slice(endElemSelectionInd);
                        startP.replaceChild(document.createTextNode(commonReplacementText), startElem);
                        endElemSelectionInd += leftTag.length + rightTag.length;
                    } else {
                        // different elements replace twice
                        leftReplacementText = startElemVal.slice(0, startElemSelectionInd) + leftTag +
                            startElemVal.slice(startElemSelectionInd);
                        rightReplacementText = endElemVal.slice(0, endElemSelectionInd) + rightTag +
                            endElemVal.slice(endElemSelectionInd);
                        startP.replaceChild(document.createTextNode(leftReplacementText), startElem);
                        endP.replaceChild(document.createTextNode(rightReplacementText), endElem);
                        endElemSelectionInd += rightTag.length;
                    }

                    // set the end selections for textinput from the edit view
                    selectionEnd += leftTag.length + rightTag.length;
                }

            } else {
                ////////////////////////////////////////////////////////////////////////////////////////////////////
                //                                             else
                ////////////////////////////////////////////////////////////////////////////////////////////////////
                var text = this.get();
                var left = text.slice(0, selectionStart);
                var selectedText = text.slice(selectionStart, selectionEnd);
                var right = text.slice(selectionEnd);

                leftTagMatch = selectedText.match(leftTagRegex);
                rightTagMatch = selectedText.match(rightTagRegex);

                if (leftTagMatch && rightTagMatch &&
                    leftTagMatch.index === 0 &&
                    rightTagMatch.index === selectionEnd - selectionStart - rightTag.length) {
                    // Remove tags from the selected text since the matches are at the beginning & end of selection
                    editedText = selectedText.replace(leftTagRegex, '').replace(rightTagRegex, '');
                    selectionEnd = selectionEnd - leftTagMatch[0].length - rightTagMatch[0].length;
                } else {
                    // Add tags around the selected text
                    editedText = leftTag + selectedText + rightTag;
                    selectionEnd = selectionEnd + leftTag.length + rightTag.length;
                }

                this.set(left + editedText + right);
            }

            this.debouncedSaveHistory();

            this.selectionIndices = {
                start: selectionStart,
                end: selectionEnd,
                startElemInd: startElemInd,
                startElemSelectionInd: startElemSelectionInd,
                endElemInd: endElemInd,
                endElemSelectionInd: endElemSelectionInd,
            };

            // this was once needed for FF, may not need it after the improved version of this editor
            // will keep this for now
            // TODO: maybe remove this v
            this.selectText();
        },

        selectText: function () {
            var rangeStartElem, rangeStartInd, rangeEndElem, rangeEndInd;
            var inputEl = this.$input[0];
            var range = window.document.createRange();
            var selectionStart = this.selectionIndices.start;
            var selectionEnd = this.selectionIndices.end;
            var parentNode = inputEl.childNodes[0];
            var selection = window.getSelection();

            if (inputEl.type && inputEl.type === 'textarea') {
                // For PostEditView .textarea is a <textarea> which requires setSelectionRange for selecting edited text
                inputEl.setSelectionRange(selectionStart, selectionEnd);

            } else if (utils.browser.isChrome() || utils.browser.isSafari() || utils.browser.isOpera() || utils.browser.isEdge()) {
                // For PostReplyView .textarea is an contentEditable <div>, requires addRange for selecting edited text
                // anchor elements and the selection indicies within to create ranges

                rangeStartElem = parentNode.childNodes[this.selectionIndices.startElemInd];
                rangeStartInd = this.selectionIndices.startElemSelectionInd;
                rangeEndElem = parentNode.childNodes[this.selectionIndices.endElemInd];
                rangeEndInd = this.selectionIndices.endElemSelectionInd;

                // create the range with the anchor elements
                range.setStart(rangeStartElem, rangeStartInd);
                range.setEnd(rangeEndElem, rangeEndInd);

                // set selection
                selection.removeAllRanges();
                selection.addRange(range);

            } else if (utils.browser.isFirefox() || utils.browser.isIE()) {

                rangeStartElem = inputEl.childNodes[this.selectionIndices.startElemInd].childNodes[0];
                rangeStartInd = this.selectionIndices.startElemSelectionInd;
                rangeEndElem = inputEl.childNodes[this.selectionIndices.endElemInd].childNodes[0];
                rangeEndInd = this.selectionIndices.endElemSelectionInd;

                // create the range with the anchor elements
                range.setStart(rangeStartElem, rangeStartInd);
                range.setEnd(rangeEndElem, rangeEndInd);

                // set selection
                // https://stackoverflow.com/questions/16160996
                // solves SCRIPT606: Could not complete the operation due to error 800a025e. error on ie
                if (!utils.browser.isIE() || (selection.rangeCount > 0 && selection.getRangeAt(0).getClientRects().length > 0))
                    selection.removeAllRanges();

                selection.addRange(range);

            } else {
                // For PostReplyView .textarea is an contentEditable <div>, requires addRange for selecting edited text
                var textEl = parentNode.childNodes[0];

                range.setStart(textEl, selectionStart);
                range.setEnd(textEl, selectionEnd);

                selection.removeAllRanges();
                selection.addRange(range);
            }

            // after adding clicking a tag (different dom node than textbox) all browsers (seemingly not edge)
            // still have the focus in textbox, edge seems to change the focus to the clicked node but the caret
            // is still in the textbox, so typing and selecting events are all not firing, refocusing fixes this
            // ie & ffr: focusing out of the textbox element in edge

            // textarea also focuses out, only used in comment editing
            if (utils.browser.isEdge() || (inputEl.type && inputEl.type === 'textarea'))
                this.focus();
        },

        debouncedSaveHistory: _.debounce(function () {
            var newValue = this.toJSON()[0];
            if (newValue !== this.history[this.historyPosition]) {
                if (this.historyPosition !== this.history.length - 1)
                    this.history = this.history.slice(0, this.historyPosition + 1);
                this.history.push(newValue);
                this.historyPosition += 1;
            }
        }, 200),

        undoTextarea: function () {
            if (this.historyPosition > 0) {
                this.historyPosition -= 1;
                this.set(this.history[this.historyPosition]);
                this.fixInputStructure();
                this.focusEndOfText();
            }
        },

        redoTextarea: function () {
            if (this.historyPosition < this.history.length - 1) { // not the last one
                this.historyPosition += 1;
                this.set(this.history[this.historyPosition]);
                this.fixInputStructure();
                this.focusEndOfText();
            }
        },
    }, {
        MAX_TEXTAREA_HEIGHT: 350,  // px
        SAVE_DRAFT_INTERVAL: 500,  // ms
        DRAFT_MAX_AGE: 1000 * 60 * 60 * 24,  // 1 day in ms
        storage: new CappedStorage(5, 'drafts.queue'),
    });

    return TextareaView;
});

